import { QueryClient, useQueryClient } from "@tanstack/react-query";
import type AgentDomain from "entities/domain/agents/agent-domain";
import InboxDomain from "entities/domain/inbox";
import { SocketConversationUnreadCountChanged } from "entities/ISocketArgs";
import {
  UnreadCountDTO,
  UnreadCountScopeDTO,
} from "entities/dto/UnreadCountDTO";
import { inboxesQueryKey } from "hooks/queries/use-inboxes";
import { useWebSocket } from "hooks/use-socket";
import { RefObject, useEffect, useRef } from "react";
import {
  AdjustedUnreadCounts,
  adjustedUnreadCounts,
  inheritedInboxAccess,
  sumUnreadCounts,
} from "util/inbox-access";
import { recordSocketUnreadCount } from "./unread-counts-socket-writes";
import { unreadCountsQueryKey, useCurrentAgent } from "./use-current-agent";

type SocketUnreadCount = SocketConversationUnreadCountChanged["unread_count"];

type SocketUnreadBucket = NonNullable<SocketUnreadCount["inboxes"]>[string];

// Coalesces a burst of events that all need the authoritative counts into
// one request, sent at most this long after the first of them. Direct cache
// writes are never delayed.
const UNREAD_COUNTS_REFETCH_DEBOUNCE_MS = 300;

/**
 * The merchant's Inbox list when it can shape counts: cached and not
 * invalidated. Age alone does not disqualify it; an invalidated list may
 * predate an access change.
 */
const usableCachedInboxes = (
  queryClient: QueryClient,
  merchantId: number
): InboxDomain[] | null => {
  const state = queryClient.getQueryState<InboxDomain[]>(
    inboxesQueryKey(merchantId)
  );

  return state?.data !== undefined && !state.isInvalidated ? state.data : null;
};

const toScopeDto = (counts: AdjustedUnreadCounts): UnreadCountScopeDTO => ({
  personal: counts.personal,
  shared: counts.shared,
  unassigned: counts.unassigned,
});

// One Inbox bucket with that Inbox's own effective capabilities, or null when
// the cached list does not report them.
const shapeInboxBucket = (
  bucket: SocketUnreadBucket,
  inbox: InboxDomain | undefined,
  agentKey: string
): AdjustedUnreadCounts | null =>
  inbox?.myAccess
    ? adjustedUnreadCounts(
        inbox.myAccess.effective,
        bucket.personal[agentKey] || 0,
        bucket.shared,
        bucket.unassigned
      )
    : null;

/**
 * The socket payload is the merchant's raw projection: per-agent personal
 * maps, and the same per Inbox. The cache holds what HTTP returns for the
 * current agent. This mirrors fuzey-api `unread_count_per_inbox_to_dto`:
 * each Inbox bucket with that Inbox's `my_access.effective`, the merchant
 * totals as each lens summed over the buckets, and without buckets the
 * merchant total with the inherited capabilities.
 *
 * Null when an Inbox's access is not known: the caller then reads the counts
 * over HTTP instead of guessing from the role.
 */
export const buildUnreadCountForCurrentAgent = (
  unreadCountChanges: SocketUnreadCount,
  currentAgent: AgentDomain,
  cachedInboxes: InboxDomain[] | null
): UnreadCountDTO | null => {
  const merchantId = unreadCountChanges.merchant_id;
  const agentKey = currentAgent.id.toString();
  const bucketEntries = Object.entries(unreadCountChanges.inboxes ?? {});

  if (bucketEntries.length === 0) {
    const merchant = adjustedUnreadCounts(
      inheritedInboxAccess({
        merchantId,
        agent: currentAgent,
        inboxes: cachedInboxes ?? undefined,
      }),
      unreadCountChanges.personal[agentKey] || 0,
      unreadCountChanges.shared,
      unreadCountChanges.unassigned
    );

    return { merchant_id: merchantId, ...toScopeDto(merchant), inboxes: null };
  }

  if (cachedInboxes === null) {
    return null;
  }

  const inboxes: NonNullable<UnreadCountDTO["inboxes"]> = {};
  const shaped: AdjustedUnreadCounts[] = [];

  for (const [inboxId, bucket] of bucketEntries) {
    const counts = shapeInboxBucket(
      bucket,
      cachedInboxes.find((inbox) => inbox.id === inboxId),
      agentKey
    );

    if (counts === null) {
      return null;
    }

    inboxes[inboxId] = toScopeDto(counts);
    shaped.push(counts);
  }

  return {
    merchant_id: merchantId,
    ...toScopeDto(sumUnreadCounts(shaped)),
    inboxes,
  };
};

// The all-merchants request useUnreadCounts reads; one already in flight is
// reused. Resolves once the counts it waited for are in the cache.
const refetchUnreadCounts = (queryClient: QueryClient): Promise<void> =>
  queryClient.invalidateQueries(
    { queryKey: unreadCountsQueryKey() },
    { cancelRefetch: false }
  );

// A request already in flight may have computed its counts before the events
// this refetch answers. It is reused, and one more request follows it, so the
// last events are never answered by older counts.
const refetchUnreadCountsAfterEvents = async (
  queryClient: QueryClient
): Promise<void> => {
  const reusesInFlight =
    queryClient.isFetching({ queryKey: unreadCountsQueryKey() }) > 0;

  await refetchUnreadCounts(queryClient);

  if (reusesInFlight) {
    await refetchUnreadCounts(queryClient);
  }
};

const flushUnreadCountsRefetch = (
  queryClient: QueryClient,
  timer: RefObject<ReturnType<typeof setTimeout> | null>
): void => {
  timer.current = null;
  void refetchUnreadCountsAfterEvents(queryClient);
};

const scheduleUnreadCountsRefetch = (
  queryClient: QueryClient,
  timer: RefObject<ReturnType<typeof setTimeout> | null>
): void => {
  // Not restarted by later events: a steady stream still gets its refetch.
  if (timer.current !== null) {
    return;
  }

  timer.current = setTimeout(
    flushUnreadCountsRefetch,
    UNREAD_COUNTS_REFETCH_DEBOUNCE_MS,
    queryClient,
    timer
  );
};

const writeUnreadCount = (
  queryClient: QueryClient,
  next: UnreadCountDTO
): void => {
  // A counts request already in flight answers for an older moment.
  recordSocketUnreadCount(next);
  queryClient.setQueryData<UnreadCountDTO[]>(
    unreadCountsQueryKey(),
    (old = []) => [
      ...old.filter((uc) => uc.merchant_id !== next.merchant_id),
      next,
    ]
  );
};

/**
 * Subscribes to `conversation_unread_count_changed` socket events and writes the
 * result straight into the unread-counts query cache.
 *
 * Writing to the cache directly (rather than routing through React state in the
 * authenticated page wrapper) keeps these high-frequency events from re-rendering
 * the whole app shell — only components that read the unread-counts query update.
 * The functional updater also merges against the latest cache, so bursts of
 * events can't clobber each other via a stale closure.
 *
 * An event for a merchant whose Inbox access is missing or invalidated (before
 * the list loads, after an access change, another merchant's) reads the
 * counts over HTTP instead, debounced. A reconnect does too: events sent
 * while disconnected are lost.
 */
export function useUnreadCountsRealtimeUpdates() {
  const queryClient = useQueryClient();
  const { data: currentAgent } = useCurrentAgent();
  const { socket, addEventHandler, removeEventHandler } = useWebSocket();
  const refetchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const handleConversationUnreadCountChanged = (
      args: SocketConversationUnreadCountChanged
    ) => {
      if (!currentAgent) return;

      const merchantId = args.unread_count.merchant_id;
      const merchantIdsAgentBelongsTo = currentAgent.merchants.map((m) => m.id);
      if (!merchantIdsAgentBelongsTo.includes(merchantId)) {
        return;
      }

      const nextUnreadCount = buildUnreadCountForCurrentAgent(
        args.unread_count,
        currentAgent,
        usableCachedInboxes(queryClient, merchantId)
      );

      if (nextUnreadCount === null) {
        scheduleUnreadCountsRefetch(queryClient, refetchTimer);
        return;
      }

      writeUnreadCount(queryClient, nextUnreadCount);
    };

    addEventHandler(
      "conversation_unread_count_changed",
      handleConversationUnreadCountChanged
    );

    return () => {
      removeEventHandler(
        "conversation_unread_count_changed",
        handleConversationUnreadCountChanged
      );
    };
  }, [queryClient, currentAgent, addEventHandler, removeEventHandler]);

  useEffect(() => {
    if (!socket) return;
    const refresh = () => {
      void refetchUnreadCountsAfterEvents(queryClient);
    };
    socket.on("connect", refresh);
    return () => {
      socket.off("connect", refresh);
    };
  }, [socket, queryClient]);

  useEffect(
    () => () => {
      if (refetchTimer.current !== null) {
        clearTimeout(refetchTimer.current);
      }
    },
    []
  );
}
